/**
 * FactoryCostPage — dedicated page cho `/factory/<code>/data/bom-cost`.
 *
 * Master data Batch Cost (table `cost_item` join `cost_group` via view
 * `v_cost_item_master`). Layout y chang BOM: TableVirtuoso + app-dataframe
 * toolbar + filter popovers + columns menu + sortable headers + BrandState.
 */
import AddIcon from '@mui/icons-material/Add'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import PaidIcon from '@mui/icons-material/Paid'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { alpha, type SxProps, type Theme } from '@mui/material/styles'
import dayjs from 'dayjs'
import { type CSSProperties, useMemo, useState } from 'react'
import { TableVirtuoso } from 'react-virtuoso'

import BrandState from '@/components/BrandState'
import ResizeHandle from '@/components/ResizeHandle'
import {
  type ColumnFilterAnchorPosition,
  type ColumnFilterKind,
  ColumnFilterIcon,
  ColumnFilterPopover,
  FilterButton,
  filterBarBtnSx,
  FilterPopover,
  SearchFilterContent,
  distinctColumnValues,
  inferColumnFilterKind,
  rowMatchesAllColumnFilters,
  rowMatchesSearch,
  useColumnFilters,
  useFilterPopover,
  useRowSelection,
} from '@/components/dataList'
import { useColumnWidths } from '@/hooks/useColumnWidths'
import { useIsMobile } from '@/hooks/useResponsive'
import { useSnackbar } from '@/hooks/useSnackbar'
import {
  useCreateFactoryCostMutation,
  useGetFactoryCostDetailQuery,
  useGetFactoryCostListQuery,
  useGetFactoryCostOptionsQuery,
  useUpdateFactoryCostMutation,
  type CostListRow,
  type CostSavePayload,
} from '@/store/api/factoryCostApi'
import { FuturesColumnsMenu } from '@/pages/futures/FuturesColumnsMenu'
import { usePreferenceColumns } from '@/utils/datasheetColumns/usePreferenceColumns'
import { titleCaseName } from '@/utils/text'
import {
  getTableSortSuffix,
  sortTableRows,
  type TableSortKind,
  type TableSortState,
  toggleTableSort,
} from '@/utils/tableSort'
import {
  FILTER_HEADER_SELECT_SX,
  HIDE_ON_MOBILE_INLINE,
  HIDE_ON_TABLET_INLINE,
  TABLE_PAGINATION_COMPACT,
} from '@/theme/responsive'

import CostFormDialog from './CostFormDialog'

const actionToolbarBtnSx: SxProps<Theme> = {
  minWidth: 0,
  px: 0.75,
  py: 0.2,
  minHeight: 26,
  fontSize: '0.72rem',
  lineHeight: 1.1,
  textTransform: 'none',
}

type ColumnDef = {
  key: string
  label: string
  kind?: TableSortKind
  align?: 'right'
  filter?: ColumnFilterKind
}

/**
 * Cột freeze khi scroll ngang — chỉ Item Code (cột định danh).
 * Skill `/table-style` §7.6: sticky-left + opaque bg + z-index → cột trước nó
 * scroll khuất phía sau khi cuộn phải. Width fallback theo viewport.
 */
const FROZEN_KEYS: ReadonlySet<string> = new Set(['cost_item_code'])
const FROZEN_FALLBACK_WIDTH_DESKTOP: Record<string, number> = { cost_item_code: 160 }
const FROZEN_FALLBACK_WIDTH_MOBILE: Record<string, number> = { cost_item_code: 140 }

const TABLE_COLUMNS: ColumnDef[] = [
  { key: 'cost_item_code', label: 'Item Code' },
  { key: 'display_code', label: 'Display Code' },
  { key: 'cost_group_name_en', label: 'Group' },
  { key: 'cost_item_name_en', label: 'Item Name' },
  { key: 'cost_item_name_vi', label: 'Item Name (VN)' },
  { key: 'unit_price', label: 'Unit Price', kind: 'currency', align: 'right' },
  { key: 'currency', label: 'Currency' },
  { key: 'price_unit', label: 'Unit' },
  { key: 'calc_method', label: 'Calc Method' },
  { key: 'valid_from', label: 'Valid From', kind: 'date' },
  { key: 'valid_to', label: 'Valid To', kind: 'date' },
  { key: 'vendor_name', label: 'Vendor' },
  { key: 'vendor_contract_no', label: 'Vendor Contract' },
  { key: 'description', label: 'Description' },
  { key: 'updated_date', label: 'Updated Date', kind: 'date' },
  { key: 'updated_by', label: 'Updated Person' },
]

const ACCESS_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const formatAccessDate = (value: unknown): string => {
  if (value == null || value === '') return ''
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return String(value)
  const day = `${d.getDate()}`.padStart(2, '0')
  const month = ACCESS_MONTHS[d.getMonth()]
  return `${day}-${month}-${d.getFullYear()}`
}

const formatCurrency = (value: unknown): string => {
  if (value == null || value === '') return ''
  const n = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(n)) return ''
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const distinctValues = (rows: CostListRow[], key: string): string[] => {
  const values = new Set<string>()
  for (const row of rows) {
    const value = (row as Record<string, unknown>)[key]
    if (value == null) continue
    const text = String(value).trim()
    if (text) values.add(text)
  }
  return [...values].sort((a, b) => a.localeCompare(b))
}

/**
 * Dòng "ngoài hiệu lực": ngày hôm nay KHÔNG nằm trong [valid_from, valid_to]
 * (so theo ngày; bound null = mở vô hạn phía đó). Dùng để bôi đỏ cảnh báo cost
 * không còn áp dụng cho hôm nay (đã hết hạn hoặc chưa tới ngày hiệu lực).
 */
const isValidityExpired = (row: CostListRow): boolean => {
  const today = dayjs().startOf('day')
  const from = row.valid_from ? dayjs(String(row.valid_from).slice(0, 10)) : null
  const to = row.valid_to ? dayjs(String(row.valid_to).slice(0, 10)) : null
  if (from && from.isValid() && today.isBefore(from, 'day')) return true
  if (to && to.isValid() && today.isAfter(to, 'day')) return true
  return false
}

const EXPIRED_CELL_BG = '#fdecea'
const EXPIRED_CELL_FG = '#b71c1c'

const cellValue = (row: CostListRow, col: ColumnDef): string => {
  const val = (row as Record<string, unknown>)[col.key]
  if (val == null || val === '') return ''
  if (col.kind === 'date') return formatAccessDate(val)
  if (col.kind === 'currency') return formatCurrency(val)
  if (typeof val === 'boolean') return val ? 'Yes' : 'No'
  if (col.key === 'vendor_name') return titleCaseName(val)
  return String(val)
}

interface FactoryCostPageProps {
  factoryCode: string
  /** Back-to-hub callback. Khi user trở về `/factory/<code>/data`. */
  onBackToHub: () => void
  canEdit: boolean
}

type DialogState =
  | { kind: 'closed' }
  | { kind: 'new' }
  | { kind: 'edit'; costItemId: number }
  | { kind: 'copy'; source: CostListRow }

const ALL_ROWS_PAGE_SIZE = -1

export default function FactoryCostPage({
  factoryCode,
  onBackToHub,
  canEdit,
}: FactoryCostPageProps) {
  const snack = useSnackbar()
  const isMobile = useIsMobile()
  const FROZEN_FALLBACK_WIDTH = isMobile ? FROZEN_FALLBACK_WIDTH_MOBILE : FROZEN_FALLBACK_WIDTH_DESKTOP

  const [costGroupFilter, setCostGroupFilter] = useState('')

  const list = useGetFactoryCostListQuery({ factoryCode })
  const options = useGetFactoryCostOptionsQuery(factoryCode)

  const { selectedId, toggleSelect } = useRowSelection<number>()
  const [dialog, setDialog] = useState<DialogState>({ kind: 'closed' })

  const [createCost, { isLoading: creating }] = useCreateFactoryCostMutation()
  const [updateCost, { isLoading: updating }] = useUpdateFactoryCostMutation()

  const editDetail = useGetFactoryCostDetailQuery(
    dialog.kind === 'edit'
      ? { factoryCode, costItemId: dialog.costItemId }
      : { factoryCode, costItemId: 0 },
    { skip: dialog.kind !== 'edit' },
  )

  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState<number>(ALL_ROWS_PAGE_SIZE)

  const { getWidth, startResize } = useColumnWidths('factory.cost.list')

  const [searchTerm, setSearchTerm] = useState('')
  const [sortState, setSortState] = useState<TableSortState<string> | null>(null)

  const filterPopover = useFilterPopover<'search'>()
  const columnFilters = useColumnFilters()
  const [colPopover, setColPopover] = useState<{
    key: string
    position: ColumnFilterAnchorPosition
  } | null>(null)

  const columnsCtrl = usePreferenceColumns<ColumnDef>({
    scope: 'factory',
    prefKey: 'columns',
    subKey: 'cost',
    allColumns: TABLE_COLUMNS,
    widthsTableKey: 'factory.cost.list',
  })
  const { visibleColumns, openMenu: openColMenu } = columnsCtrl

  const baseRows = useMemo(() => list.data?.items ?? [], [list.data])

  const filteredRows = useMemo(
    () =>
      baseRows.filter((row) => {
        if (!rowMatchesSearch(row, searchTerm, TABLE_COLUMNS)) return false
        if (costGroupFilter && row.cost_group_code !== costGroupFilter) return false
        if (!rowMatchesAllColumnFilters(row, columnFilters.filters)) return false
        return true
      }),
    [baseRows, searchTerm, costGroupFilter, columnFilters.filters],
  )

  const hasActiveFilters =
    !!searchTerm.trim() || !!costGroupFilter || columnFilters.hasAny
  const activeFilterCount =
    (searchTerm.trim() ? 1 : 0) + (costGroupFilter ? 1 : 0) + columnFilters.activeKeys.length
  const clearAllFilters = () => {
    setSearchTerm('')
    setCostGroupFilter('')
    columnFilters.clearAll()
    setPage(0)
    filterPopover.close()
  }

  const popoverColumn = useMemo(
    () => (colPopover ? TABLE_COLUMNS.find((c) => c.key === colPopover.key) ?? null : null),
    [colPopover],
  )
  const popoverKind = useMemo<ColumnFilterKind>(
    () => (popoverColumn ? inferColumnFilterKind(popoverColumn as never, baseRows) : 'text'),
    [popoverColumn, baseRows],
  )
  const popoverOptions = useMemo(
    () =>
      popoverKind === 'checkboxes' && colPopover
        ? distinctColumnValues(baseRows, colPopover.key)
        : undefined,
    [popoverKind, colPopover, baseRows],
  )

  const sortedRows = useMemo(
    () => sortTableRows(filteredRows, sortState, TABLE_COLUMNS),
    [filteredRows, sortState],
  )

  const rows = useMemo(() => {
    if (rowsPerPage === -1) return sortedRows
    const start = page * rowsPerPage
    return sortedRows.slice(start, start + rowsPerPage)
  }, [page, rowsPerPage, sortedRows])

  const costGroupOptions = useMemo(() => options.data?.costGroups ?? [], [options.data?.costGroups])

  // Default width mỗi cột = đo theo text thật (header + sample data) → cột ngắn
  // 1 dòng, cột dài clamp 260 rồi wrap. Sàn date 124 (§7.11 convert-table).
  const autoColWidths = useMemo<Record<string, number>>(() => {
    const CHAR_PX = 7.7,
      HEADER_PAD = 52,
      CELL_PAD = 28,
      MIN = 100,
      MAX = 260
    const sample = filteredRows.slice(0, 400)
    const out: Record<string, number> = {}
    for (const col of visibleColumns) {
      let bodyMax = 0
      for (const r of sample) {
        const len = cellValue(r, col).length
        if (len > bodyMax) bodyMax = len
      }
      const headerPx = (col.label ?? col.key).length * CHAR_PX + HEADER_PAD
      const bodyPx = bodyMax * CHAR_PX + CELL_PAD
      const minForType = (col.kind as string) === 'date' || (col.kind as string) === 'datetime' ? 124 : MIN
      out[col.key] = Math.round(Math.min(MAX, Math.max(minForType, headerPx, bodyPx)))
    }
    return out
  }, [visibleColumns, filteredRows])

  const minTableWidth = useMemo(() => {
    let sum = 0
    for (const col of visibleColumns) {
      const k = col.key
      sum += getWidth(k) ?? (FROZEN_KEYS.has(k) ? FROZEN_FALLBACK_WIDTH[k] : autoColWidths[k]) ?? 120
    }
    return Math.max(720, sum)
  }, [visibleColumns, getWidth, FROZEN_FALLBACK_WIDTH, autoColWidths])
  const gridKey = `${rowsPerPage}:${page}:${filteredRows.length}:${visibleColumns.length}`

  const toggleSort = (key: string) => {
    setSortState((current) => toggleTableSort(current, key))
    setPage(0)
  }

  const selectedRow = useMemo(
    () => baseRows.find((r) => r.cost_item_id === selectedId) ?? null,
    [baseRows, selectedId],
  )

  const handleSave = async (payload: CostSavePayload) => {
    if (!canEdit) {
      snack.error('Read-only — you do not have edit permission for this factory.')
      return
    }
    try {
      if (dialog.kind === 'new' || dialog.kind === 'copy') {
        await createCost({ factoryCode, body: payload }).unwrap()
        snack.success(dialog.kind === 'copy' ? 'Cost item copied' : 'Cost item created')
      } else if (dialog.kind === 'edit') {
        await updateCost({
          factoryCode,
          costItemId: dialog.costItemId,
          body: payload,
        }).unwrap()
        snack.success('Cost item updated')
      }
      setDialog({ kind: 'closed' })
    } catch (err) {
      const msg =
        err && typeof err === 'object' && 'data' in err
          ? String((err as { data?: { detail?: string } }).data?.detail ?? 'Save failed')
          : 'Save failed'
      snack.error(msg)
    }
  }

  const handleEditClick = () => {
    if (!canEdit) return
    if (!selectedRow) {
      snack.warning('Please choose a record!')
      return
    }
    setDialog({ kind: 'edit', costItemId: selectedRow.cost_item_id })
  }

  // Copy: clone dòng đang chọn sang record mới (suggest Valid From/To contract
  // mới trong dialog). CostDetail = CostListRow nên truyền thẳng selectedRow.
  const handleCopyClick = () => {
    if (!canEdit) return
    if (!selectedRow) {
      snack.warning('Please choose a record!')
      return
    }
    setDialog({ kind: 'copy', source: selectedRow })
  }

  const saving = creating || updating

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      {/* Action + filter bar */}
      <Stack
        direction="row"
        flexWrap="wrap"
        useFlexGap
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 0.5 }}
      >
        <Stack direction="row" spacing={0.375} flexWrap="wrap" useFlexGap alignItems="center" sx={{ minWidth: 0 }}>
          <Button
            size="small"
            variant="outlined"
            color="inherit"
            startIcon={<ArrowBackIcon sx={{ fontSize: 14 }} />}
            onClick={onBackToHub}
            sx={filterBarBtnSx}
          >
            Datasets
          </Button>
          {/* Page title — solid accent pill (icon + label trên cùng background
              accent color, text trắng) để user vào page là nhận ra context ngay. */}
          <Stack
            direction="row"
            spacing={0.625}
            alignItems="center"
            sx={{
              mx: 0.5,
              px: 1,
              py: 0.5,
              borderRadius: 1,
              backgroundColor: '#5a7a4e',
              color: '#fff',
              boxShadow: `0 1px 3px ${alpha('#5a7a4e', 0.45)}`,
            }}
          >
            <PaidIcon sx={{ fontSize: '0.95rem', color: '#fff' }} />
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 800,
                fontSize: '0.85rem',
                color: '#fff',
                textTransform: 'uppercase',
                letterSpacing: 0.6,
                whiteSpace: 'nowrap',
                lineHeight: 1.1,
              }}
            >
              Batch Cost
            </Typography>
          </Stack>
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.5 }} />
          <Typography variant="caption" color="text.secondary" sx={{ mr: 0.25, fontWeight: 400 }}>
            Filter
          </Typography>
          <FilterButton
            label="Search"
            active={!!searchTerm.trim()}
            onClick={(e) => filterPopover.open('search', e)}
          />
          {hasActiveFilters && (
            <Button size="small" variant="text" color="secondary" onClick={clearAllFilters} sx={filterBarBtnSx}>
              Clear All{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
            </Button>
          )}
          <Button
            size="small"
            variant="outlined"
            endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />}
            onClick={(e) => openColMenu(e.currentTarget)}
            sx={filterBarBtnSx}
            title="Columns"
          >
            <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Columns</Box>
            <Box component="span" sx={{ display: { xs: 'inline', md: 'none' } }}>Cols</Box>
          </Button>
        </Stack>
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap justifyContent="flex-end" alignItems="center">
          <TextField
            label="Cost Group"
            select
            size="small"
            value={costGroupFilter}
            onChange={(e) => { setCostGroupFilter(String(e.target.value)); setPage(0) }}
            InputLabelProps={{ shrink: true }}
            sx={{ ...FILTER_HEADER_SELECT_SX, width: { xs: 130, sm: 170 } }}
          >
            <MenuItem value="" sx={{ fontSize: '0.78rem' }}>
              <em>All</em>
            </MenuItem>
            {costGroupOptions.map((g) => (
              <MenuItem key={g.cost_group_code} value={g.cost_group_code} sx={{ fontSize: '0.78rem' }}>
                {g.cost_group_name_en || g.cost_group_code}
              </MenuItem>
            ))}
          </TextField>
          <Tooltip title={canEdit ? 'New' : 'Read-only — contact admin'}>
            <span>
              <Button
                variant="contained"
                size="small"
                color="success"
                sx={actionToolbarBtnSx}
                disabled={!canEdit || saving}
                onClick={() => setDialog({ kind: 'new' })}
                startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                title="New"
              >
                <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>New</Box>
              </Button>
            </span>
          </Tooltip>
          <Button
            variant="outlined"
            size="small"
            color="warning"
            sx={actionToolbarBtnSx}
            disabled={!canEdit || !selectedRow || saving}
            onClick={handleEditClick}
            startIcon={<EditOutlinedIcon sx={{ fontSize: 16 }} />}
            title="Edit"
          >
            <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Edit</Box>
          </Button>
          <Button
            variant="outlined"
            size="small"
            color="primary"
            sx={actionToolbarBtnSx}
            disabled={!canEdit || !selectedRow || saving}
            onClick={handleCopyClick}
            startIcon={<ContentCopyIcon sx={{ fontSize: 16 }} />}
            title="Copy for new contract"
          >
            <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Copy</Box>
          </Button>
        </Stack>
      </Stack>

      <Divider sx={{ mb: 0.5 }} />

      {list.isError ? (
        <Alert severity="error" sx={{ mb: 1 }}>
          Failed to load cost item list.
        </Alert>
      ) : null}

      <Box className="app-dataframe" sx={{ flex: 1, minHeight: 0, height: 'auto' }}>
        {/* Toolbar — pagination + counts */}
        <Box
          className="app-dataframe-toolbar"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 0.5,
            flexWrap: 'wrap',
            minHeight: 24,
            py: 0,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={0.4} sx={{ flexShrink: 0 }}>
            {(list.isLoading || list.isFetching) && <CircularProgress size={15} />}
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}
            >
              Total: {filteredRows.length}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}
            >
              Loaded: {baseRows.length}
            </Typography>
            <TablePagination
              component="div"
              count={filteredRows.length}
              page={rowsPerPage === -1 ? 0 : page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(Number(e.target.value))
                setPage(0)
              }}
              rowsPerPageOptions={[20, 50, 100, { label: 'All', value: -1 }]}
              labelRowsPerPage="Rows"
              sx={TABLE_PAGINATION_COMPACT}
            />
          </Stack>
        </Box>

        <FilterPopover
          open={filterPopover.state !== null}
          anchorEl={filterPopover.state?.anchor ?? null}
          onClose={filterPopover.close}
          maxWidth={320}
        >
          {filterPopover.isOpen('search') && (
            <SearchFilterContent
              value={searchTerm}
              onChange={(v) => { setSearchTerm(v); setPage(0) }}
              placeholder="Code, name, vendor..."
              label="Search Cost Item"
              onClear={filterPopover.close}
            />
          )}
        </FilterPopover>

        <ColumnFilterPopover
          open={colPopover !== null}
          anchorPosition={colPopover?.position ?? null}
          onClose={() => setColPopover(null)}
          label={popoverColumn?.label ?? ''}
          kind={popoverKind === 'none' ? 'text' : popoverKind}
          value={colPopover ? columnFilters.filters[colPopover.key] : undefined}
          onChange={(v) => {
            if (!colPopover) return
            columnFilters.setFilter(colPopover.key, v)
            setPage(0)
          }}
          options={popoverOptions}
        />

        {/* Virtualized table */}
        <Box sx={{ flex: 1, minHeight: { xs: 280, sm: 320 }, overflow: 'hidden' }}>
          {list.isError ? (
            <BrandState
              tone="error"
              title="Could not load cost item list"
              description="Please refresh the page or contact the system administrator if the issue continues."
            />
          ) : list.isLoading && baseRows.length === 0 ? (
            <BrandState tone="loading" title="Loading…" description="Please wait." />
          ) : rows.length === 0 ? (
            <BrandState
              title="No cost items found"
              description="Try adjusting the search or Calc Method filter above, or create a new cost item."
            />
          ) : (
            <TableVirtuoso
              key={gridKey}
              style={{ height: '100%' }}
              data={rows}
              fixedHeaderContent={() => (
                <TableRow>
                  {visibleColumns.map((col) => {
                    const isFrozen = FROZEN_KEYS.has(col.key)
                    const w = getWidth(col.key) ?? (isFrozen ? FROZEN_FALLBACK_WIDTH[col.key] : autoColWidths[col.key])
                    // Inline style cho sticky positioning — specificity 1,0,0,0 luôn
                    // thắng MUI `.MuiTableCell-stickyHeader` z-index 2, tránh stacking
                    // context isolation trong TableVirtuoso.
                    const headerStyle: CSSProperties = {}
                    if (w) { headerStyle.width = w; headerStyle.minWidth = w; headerStyle.maxWidth = w }
                    if (isFrozen) {
                      headerStyle.position = 'sticky'
                      headerStyle.top = 0
                      headerStyle.left = 0
                      headerStyle.zIndex = 100
                    }
                    return (
                      <TableCell
                        key={col.key}
                        align={col.align}
                        className="app-table-header-cell"
                        onClick={() => toggleSort(col.key)}
                        style={Object.keys(headerStyle).length ? headerStyle : undefined}
                        sx={{
                          whiteSpace: 'nowrap',
                          cursor: 'pointer',
                          // Cho phép bôi đen + copy TÊN CỘT (click-sort vẫn chạy).
                          userSelect: 'text',
                          ...(isFrozen ? {} : { position: 'relative' }),
                        }}
                      >
                        {col.label}
                        {getTableSortSuffix(sortState, col.key)}
                        <ColumnFilterIcon
                          active={columnFilters.isActive(col.key)}
                          onClick={(position) => setColPopover({ key: col.key, position })}
                          ariaLabel={`Filter ${col.label}`}
                        />
                        <ResizeHandle onMouseDown={(e) => startResize(col.key, e)} />
                      </TableCell>
                    )
                  })}
                </TableRow>
              )}
              itemContent={(_: number, row: CostListRow) => {
                const expired = isValidityExpired(row)
                return visibleColumns.map((col) => {
                  const isFrozen = FROZEN_KEYS.has(col.key)
                  const isValidityCol = col.key === 'valid_from' || col.key === 'valid_to'
                  const val = cellValue(row, col)
                  const w = getWidth(col.key) ?? (isFrozen ? FROZEN_FALLBACK_WIDTH[col.key] : autoColWidths[col.key])
                  // Inline bg đỏ thắng CSS sticky `.app-sticky-col { background:#fff }`
                  // (non-!important) → cột freeze cũng đỏ theo cả dòng.
                  const cellStyle: CSSProperties = {}
                  if (w) { cellStyle.width = w; cellStyle.minWidth = w; cellStyle.maxWidth = w }
                  if (expired) { cellStyle.backgroundColor = EXPIRED_CELL_BG; cellStyle.color = EXPIRED_CELL_FG }
                  const wrap = true
                  if (wrap) {
                    return (
                      <TableCell
                        key={col.key}
                        align={col.align}
                        className={isFrozen ? 'app-sticky-col' : undefined}
                        style={Object.keys(cellStyle).length ? cellStyle : undefined}
                        sx={{
                          ...(expired ? { fontWeight: isValidityCol ? 700 : 600 } : {}),
                          whiteSpace: 'normal',
                          overflowWrap: 'anywhere',
                          py: 0.5,
                          verticalAlign: 'top',
                        }}
                      >
                        <Box component="span" className="app-table-cell-wrap">
                          {val || '-'}
                        </Box>
                      </TableCell>
                    )
                  }
                  return (
                    <TableCell
                      key={col.key}
                      align={col.align}
                      className={isFrozen ? 'app-sticky-col' : undefined}
                      style={Object.keys(cellStyle).length ? cellStyle : undefined}
                      sx={{
                        whiteSpace: 'nowrap',
                        py: 0.5,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        verticalAlign: 'top',
                        ...(expired ? { fontWeight: isValidityCol ? 700 : 600 } : {}),
                      }}
                    >
                      <Box
                        component="span"
                        className="app-table-cell-text"
                        title={
                          expired && isValidityCol
                            ? `${val || '-'} — outside validity period (today is not within Valid From–To)`
                            : val || '-'
                        }
                        sx={{ display: 'inline-block', width: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}
                      >
                        {val || '-'}
                      </Box>
                    </TableCell>
                  )
                })
              }}
              components={{
                Table: (props: any) => (
                  <Table {...props} size="small" stickyHeader sx={{ minWidth: minTableWidth, width: '100%' }} />
                ),
                TableHead: ((props: any) => <TableHead {...props} component="thead" />) as any,
                TableBody: ((props: any) => <TableBody {...props} component="tbody" />) as any,
                TableRow: (props: any) => {
                  const idx = Number((props as any)['data-item-index'])
                  const row = Number.isFinite(idx) ? rows[idx] : null
                  const id = row?.cost_item_id ?? null
                  const isSelected = id != null && id === selectedId
                  return (
                    <TableRow
                      {...props}
                      hover
                      selected={isSelected}
                      sx={{ cursor: 'pointer' }}
                      onClick={() => {
                        if (id != null) toggleSelect(id)
                      }}
                      onDoubleClick={() => {
                        if (!canEdit || row == null) return
                        setDialog({ kind: 'edit', costItemId: row.cost_item_id })
                      }}
                    />
                  )
                },
              }}
            />
          )}
        </Box>
      </Box>

      <CostFormDialog
        open={dialog.kind !== 'closed'}
        mode={dialog.kind === 'new' ? 'new' : dialog.kind === 'copy' ? 'copy' : 'edit'}
        detail={
          dialog.kind === 'edit'
            ? editDetail.data ?? null
            : dialog.kind === 'copy'
            ? dialog.source
            : null
        }
        detailLoading={dialog.kind === 'edit' && editDetail.isFetching}
        saving={saving}
        options={options.data ?? null}
        optionsLoading={options.isFetching}
        canEdit={canEdit}
        onClose={() => setDialog({ kind: 'closed' })}
        onSave={handleSave}
      />

      <FuturesColumnsMenu controller={columnsCtrl} />
    </Box>
  )
}
